import { useState, useRef, useCallback, useEffect } from "react";

import { motion } from "framer-motion";

import {
  isMessageFromPreviewFrame,
  postToPreviewFrame,
  scrollPreviewBy,
} from "../../utils/previewMessaging";

import type { DrawingRect, TaggingMode } from "./tagging.types";

interface HoverElement {
  x: number;
  y: number;
  width: number;
  height: number;
  tagName?: string;
  selector?: string;
}

interface TaggingAnnotationOverlayProps {
  onAnnotationCreate: (rect: {
    x: number;
    y: number;
    width: number;
    height: number;
    elementSelector?: string;
    createdAtScrollX?: number;
    createdAtScrollY?: number;
  }) => void;
  isActive: boolean;
  previewFrameRef: React.RefObject<HTMLIFrameElement | null>;
  taggingMode: TaggingMode;
}

const ELEMENT_QUERY_FALLBACK_TIMEOUT_MS = 300;

export function TaggingAnnotationOverlay({
  onAnnotationCreate,
  isActive,
  previewFrameRef,
  taggingMode,
}: TaggingAnnotationOverlayProps) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingRect, setDrawingRect] = useState<DrawingRect | null>(null);
  const [hoveredElement, setHoveredElement] = useState<HoverElement | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const hoveredElementRef = useRef<HoverElement | null>(null);

  const iframeScrollRef = useRef({ x: 0, y: 0 });

  // Pending draw rect waiting for element query response
  const pendingDrawRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isMessageFromPreviewFrame(event, previewFrameRef.current)) {
        return;
      }

      if (!event.data?.type) return;

      if (event.data.type === 'element-hover' && isActive && !isDrawing && taggingMode !== 'view') {
        const { viewportBounds, tagName, selector } = event.data;
        if (!overlayRef.current) return;
        const overlayRect = overlayRef.current.getBoundingClientRect();
        setHoveredElement({
          x: (viewportBounds.x / overlayRect.width) * 100,
          y: (viewportBounds.y / overlayRect.height) * 100,
          width: (viewportBounds.width / overlayRect.width) * 100,
          height: (viewportBounds.height / overlayRect.height) * 100,
          tagName,
          selector,
        });
        hoveredElementRef.current = {
          x: (viewportBounds.x / overlayRect.width) * 100,
          y: (viewportBounds.y / overlayRect.height) * 100,
          width: (viewportBounds.width / overlayRect.width) * 100,
          height: (viewportBounds.height / overlayRect.height) * 100,
          tagName,
          selector,
        };
      } else if (event.data.type === 'element-hover-end') {
        setHoveredElement(null);
        hoveredElementRef.current = null;
      } else if (event.data.type === 'iframe-scroll') {
        iframeScrollRef.current = { x: event.data.scrollX ?? 0, y: event.data.scrollY ?? 0 };
      }
      // Response from draw-mode element query
      else if (event.data.type === 'elements-in-rect-response') {
        const { selector, bounds } = event.data;
        if (selector && bounds && overlayRef.current) {
          const overlayRect = overlayRef.current.getBoundingClientRect();
          onAnnotationCreate({
            x: (bounds.x / overlayRect.width) * 100,
            y: (bounds.y / overlayRect.height) * 100,
            width: (bounds.width / overlayRect.width) * 100,
            height: (bounds.height / overlayRect.height) * 100,
            elementSelector: selector,
            createdAtScrollX: iframeScrollRef.current.x,
            createdAtScrollY: iframeScrollRef.current.y,
          });
        } else if (pendingDrawRef.current) {
          onAnnotationCreate({
            ...pendingDrawRef.current,
            createdAtScrollX: iframeScrollRef.current.x,
            createdAtScrollY: iframeScrollRef.current.y,
          });
        }
        pendingDrawRef.current = null;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isActive, isDrawing, onAnnotationCreate, previewFrameRef, taggingMode]);

  const getRelativeCoordinates = useCallback((e: React.MouseEvent) => {
    if (!overlayRef.current) return { x: 0, y: 0 };
    const rect = overlayRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isActive || taggingMode !== 'draw') return;
      const coords = getRelativeCoordinates(e);
      setIsDrawing(true);
      setHoveredElement(null);
      setDrawingRect({ startX: coords.x, startY: coords.y, endX: coords.x, endY: coords.y });
    },
    [isActive, taggingMode, getRelativeCoordinates]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDrawing && drawingRect) {
        const coords = getRelativeCoordinates(e);
        setDrawingRect((prev) => (prev ? { ...prev, endX: coords.x, endY: coords.y } : null));
        return;
      }
      if (isActive && taggingMode === 'draw' && previewFrameRef?.current?.contentWindow && overlayRef.current) {
        const overlayRect = overlayRef.current.getBoundingClientRect();
        postToPreviewFrame(
          previewFrameRef.current,
          {
            type: 'annotation-hover-probe',
            clientX: e.clientX - overlayRect.left,
            clientY: e.clientY - overlayRect.top,
          },
          { queueUntilReady: true }
        );
      }
    },
    [isDrawing, drawingRect, getRelativeCoordinates, isActive, taggingMode, previewFrameRef]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !drawingRect) return;
    setIsDrawing(false);
    const width = Math.abs(drawingRect.endX - drawingRect.startX);
    const height = Math.abs(drawingRect.endY - drawingRect.startY);

    if (width > 2 && height > 2 && overlayRef.current) {
      const overlayRect = overlayRef.current.getBoundingClientRect();
      const pxX = (Math.min(drawingRect.startX, drawingRect.endX) / 100) * overlayRect.width;
      const pxY = (Math.min(drawingRect.startY, drawingRect.endY) / 100) * overlayRect.height;
      const pxW = (width / 100) * overlayRect.width;
      const pxH = (height / 100) * overlayRect.height;

      pendingDrawRef.current = {
        x: Math.min(drawingRect.startX, drawingRect.endX),
        y: Math.min(drawingRect.startY, drawingRect.endY),
        width,
        height,
      };
      
      postToPreviewFrame(
        previewFrameRef.current,
        {
          type: 'query-elements-in-rect',
          rect: { x: pxX, y: pxY, width: pxW, height: pxH },
        },
        { queueUntilReady: true }
      );

      // Fallback: If no iframe handles the message, create the annotation directly using local overlay coords
      setTimeout(() => {
        if (pendingDrawRef.current) {
          onAnnotationCreate({
            ...pendingDrawRef.current,
            createdAtScrollX: iframeScrollRef.current.x,
            createdAtScrollY: iframeScrollRef.current.y,
          });
          pendingDrawRef.current = null;
        }
      }, ELEMENT_QUERY_FALLBACK_TIMEOUT_MS);
    } else if (hoveredElementRef.current) {
      onAnnotationCreate({
        x: hoveredElementRef.current.x,
        y: hoveredElementRef.current.y,
        width: hoveredElementRef.current.width,
        height: hoveredElementRef.current.height,
        elementSelector: hoveredElementRef.current.selector,
        createdAtScrollX: iframeScrollRef.current.x,
        createdAtScrollY: iframeScrollRef.current.y,
      });
      setHoveredElement(null);
      hoveredElementRef.current = null;
    }
    setDrawingRect(null);
  }, [isDrawing, drawingRect, previewFrameRef, onAnnotationCreate]);

  const handleElementClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isActive || taggingMode !== 'element' || !hoveredElement) return;
      e.preventDefault();
      e.stopPropagation();
      onAnnotationCreate({
        x: hoveredElement.x,
        y: hoveredElement.y,
        width: hoveredElement.width,
        height: hoveredElement.height,
        elementSelector: hoveredElement.selector,
        createdAtScrollX: iframeScrollRef.current.x,
        createdAtScrollY: iframeScrollRef.current.y,
      });
      setHoveredElement(null);
    },
    [isActive, taggingMode, hoveredElement, onAnnotationCreate]
  );

  const isDrawMode = taggingMode === 'draw';
  const isElementMode = taggingMode === 'element';
  const isViewMode = taggingMode === 'view';

  return (
    <div
      ref={overlayRef}
      role="presentation"
      className={`absolute inset-0 z-20 ${
        isViewMode
          ? 'pointer-events-none'
          : isActive && (isDrawMode || isElementMode)
            ? isDrawMode
              ? 'cursor-crosshair'
              : 'cursor-pointer'
            : 'pointer-events-none'
      }`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onWheel={(e) => {
        if (isDrawMode) {
          e.preventDefault();
          scrollPreviewBy(previewFrameRef.current, e.deltaX, e.deltaY);
        }
      }}
      onMouseLeave={() => {
        if (isDrawing) { setIsDrawing(false); setDrawingRect(null); }
        setHoveredElement(null);
        hoveredElementRef.current = null;
        postToPreviewFrame(
          previewFrameRef.current,
          { type: 'annotation-hover-leave' },
          { queueUntilReady: true }
        );
      }}
    >

      {/* Element hover highlight */}
      {hoveredElement && !isDrawing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.9 }}
          transition={{ duration: 0.1 }}
          className={`absolute border-2 rounded-sm ${
            taggingMode === 'element'
              ? 'border-amber-400/70 bg-amber-400/10 cursor-pointer pointer-events-auto'
              : 'border-amber-400/40 bg-amber-400/5 pointer-events-none'
          }`}
          style={{
            left: `${hoveredElement.x}%`,
            top: `${hoveredElement.y}%`,
            width: `${hoveredElement.width}%`,
            height: `${hoveredElement.height}%`,
            boxShadow: '0 0 0 1px rgba(96,165,250,0.3), inset 0 0 0 1px rgba(96,165,250,0.15)',
          }}
          onClick={handleElementClick}
        >
          {hoveredElement.tagName && (
            <span className="absolute -top-5 left-0 text-[10px] font-mono bg-amber-400/90 text-white px-1.5 py-0.5 rounded-sm leading-none whitespace-nowrap">
              {hoveredElement.tagName}
            </span>
          )}
        </motion.div>
      )}

      {/* Drawing rectangle (only while actively drawing) */}
      {drawingRect && (
        <div
          className="absolute border-2 border-amber-400 bg-amber-400/10 rounded"
          style={{
            left: `${Math.min(drawingRect.startX, drawingRect.endX)}%`,
            top: `${Math.min(drawingRect.startY, drawingRect.endY)}%`,
            width: `${Math.abs(drawingRect.endX - drawingRect.startX)}%`,
            height: `${Math.abs(drawingRect.endY - drawingRect.startY)}%`,
          }}
        />
      )}
    </div>
  );
}
